// ====================================================================
// WATER — brooks, footbridges and a fishing dock
// ====================================================================
// The map used to be bone-dry apart from the Grove's oasis pond. This module
// adds the connective water the design plan wants (§2, "distinct biome reads"):
// a shallow brook that the south road crosses on a little footbridge on the way
// to Numberton, and a fishing-hole pond out on the SW diagonal. It reuses the
// exact one-plane trick the Grove pond proved (grove.js): a translucent water
// ribbon on a sandy bank, a handful of draw calls for the whole system.
//
// Chromebook discipline: the brook is TWO merged meshes (one sand ribbon, one
// water ribbon) regardless of how many bends it has; the footbridge is ONE
// merged vertex-coloured mesh; the pond is one disc + one shore ring. All water
// is decorative (non-collidable) — kids paddle straight through, cozy by design,
// same as the oasis. The shimmer is a shared opacity pulse, no per-frame alloc.
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { LOW } from '../core/tier.js';
import { PALETTE } from '../core/palette.js';
import { heightAt } from './heightfield.js';

const _waters = [];   // { mesh, baseOpacity } — shimmer targets
let _sharedGlints = [];

// A ribbon of quads following a [x,z] centreline, offset ±halfWidth along each
// vertex's averaged perpendicular, laid a hair above the sampled ground.
function ribbonGeometry(points, halfWidth, lift) {
  const n = points.length;
  const left = [], right = [];
  for (let i = 0; i < n; i++) {
    const p = points[i];
    const a = points[Math.max(0, i - 1)];
    const b = points[Math.min(n - 1, i + 1)];
    let dx = b[0] - a[0], dz = b[1] - a[1];
    const len = Math.hypot(dx, dz) || 1;
    dx /= len; dz /= len;
    const px = -dz, pz = dx;                       // perpendicular
    const gy = heightAt(p[0], p[1]) + lift;
    left.push([p[0] + px * halfWidth, gy, p[1] + pz * halfWidth]);
    right.push([p[0] - px * halfWidth, gy, p[1] - pz * halfWidth]);
  }
  const pos = [];
  for (let i = 0; i < n - 1; i++) {
    const l0 = left[i], l1 = left[i + 1], r0 = right[i], r1 = right[i + 1];
    pos.push(...l0, ...r0, ...l1);                 // two tris per segment
    pos.push(...r0, ...r1, ...l1);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.computeVertexNormals();
  return geo;
}

// A shallow brook: a sandy bank ribbon with a narrower water ribbon on top.
// `points` is the [x,z] centreline; width is the water's full width.
export function buildBrook(ctx, points, width = 1.8) {
  // The ribbon triangles can wind either way depending on the path direction, so
  // both faces are drawn (DoubleSide) — otherwise the top view culls the surface.
  const bank = new THREE.Mesh(
    ribbonGeometry(points, width * 0.72, 0.02),
    new THREE.MeshLambertMaterial({ color: PALETTE.sand, side: THREE.DoubleSide })
  );
  bank.receiveShadow = !LOW;
  ctx.propsGroup.add(bank);

  const waterGeo = ribbonGeometry(points, width * 0.5, 0.06);
  const water = new THREE.Mesh(
    waterGeo,
    new THREE.MeshLambertMaterial({ color: PALETTE.water, transparent: true, opacity: 0.8, side: THREE.DoubleSide })
  );
  ctx.propsGroup.add(water);
  _waters.push({ mesh: water, baseOpacity: 0.8 });

  // A few bright glints riding the surface, drawn from a shared sprite pool so
  // the whole map's water shimmer is one extra material.
  for (let i = 1; i < points.length - 1; i += 2) {
    const p = points[i];
    addGlint(ctx, p[0], heightAt(p[0], p[1]) + 0.09, p[1]);
  }
  return water;
}

// The fishing-hole pond: one water disc on a sand shore, plus a couple of glints.
export function buildFishingHole(ctx, x, z, r = 2.6) {
  const gy = heightAt(x, z);
  const shore = new THREE.Mesh(
    new THREE.RingGeometry(r, r + 0.9, 26),
    new THREE.MeshLambertMaterial({ color: PALETTE.sand })
  );
  shore.rotation.x = -Math.PI / 2;
  shore.position.set(x, gy + 0.015, z);
  ctx.propsGroup.add(shore);

  const water = new THREE.Mesh(
    new THREE.CircleGeometry(r, 28),
    new THREE.MeshLambertMaterial({ color: PALETTE.water, transparent: true, opacity: 0.82 })
  );
  water.rotation.x = -Math.PI / 2;
  water.position.set(x, gy + 0.04, z);
  ctx.propsGroup.add(water);
  _waters.push({ mesh: water, baseOpacity: 0.82 });

  addGlint(ctx, x - r * 0.3, gy + 0.07, z - r * 0.2);
  addGlint(ctx, x + r * 0.35, gy + 0.07, z + r * 0.3);
  return { x, z, r, gy };
}

// A tiny reused round glint sprite (mirrors the atmosphere motes) — cheap and
// only a couple per water body.
let _glintTex = null;
function glintTexture() {
  if (_glintTex) return _glintTex;
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const g = c.getContext('2d');
  const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  grad.addColorStop(0, 'rgba(255,255,255,0.9)');
  grad.addColorStop(0.5, 'rgba(230,250,255,0.35)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, 64, 64);
  _glintTex = new THREE.CanvasTexture(c);
  return _glintTex;
}
function addGlint(ctx, x, y, z) {
  const s = new THREE.Sprite(new THREE.SpriteMaterial({
    map: glintTexture(), transparent: true, opacity: 0.5, depthWrite: false, fog: true,
  }));
  const sc = 0.6 + Math.random() * 0.3;
  s.scale.set(sc, sc, 1);
  s.position.set(x, y, z);
  ctx.propsGroup.add(s);
  _sharedGlints.push({ sprite: s, phase: Math.random() * Math.PI * 2, base: 0.5 });
}

// A plank footbridge that carries a path over a brook. `yaw` turns the deck so
// its long axis (local +z, the travel direction) follows the path; span is the
// crossing length, width the deck width. One merged vertex-coloured mesh,
// decorative (the flat road under it is what the player actually stands on).
export function buildFootbridge(ctx, x, z, yaw = 0, span = 4, width = 2.3) {
  const parts = [];
  const tint = (geo, hex) => {
    const c = new THREE.Color(hex);
    const arr = new Float32Array(geo.attributes.position.count * 3);
    for (let i = 0; i < arr.length; i += 3) { arr[i] = c.r; arr[i + 1] = c.g; arr[i + 2] = c.b; }
    geo.setAttribute('color', new THREE.BufferAttribute(arr, 3));
    return geo;
  };
  // Deck: a row of planks (thin boxes across the travel direction) so it reads
  // as boards, not a slab.
  const planks = Math.max(5, Math.round(span / 0.45));
  for (let i = 0; i < planks; i++) {
    const pz = -span / 2 + (i + 0.5) * (span / planks);
    const g = new THREE.BoxGeometry(width, 0.09, span / planks * 0.82);
    g.translate(0, 0.12, pz);
    parts.push(tint(g, i % 2 ? PALETTE.woodLight : PALETTE.wood));
  }
  // Two rails on little posts down each side.
  for (const side of [-1, 1]) {
    const rail = new THREE.BoxGeometry(0.08, 0.09, span * 0.96);
    rail.translate(side * (width / 2 - 0.06), 0.42, 0);
    parts.push(tint(rail, PALETTE.woodDark));
    for (const pz of [-span / 2 + 0.25, 0, span / 2 - 0.25]) {
      const post = new THREE.BoxGeometry(0.09, 0.34, 0.09);
      post.translate(side * (width / 2 - 0.06), 0.25, pz);
      parts.push(tint(post, PALETTE.woodDark));
    }
  }
  const mesh = new THREE.Mesh(mergeGeometries(parts, false),
    new THREE.MeshLambertMaterial({ vertexColors: true }));
  for (const g of parts) g.dispose();
  mesh.castShadow = !LOW;
  mesh.receiveShadow = !LOW;
  const group = new THREE.Group();
  group.add(mesh);
  group.position.set(x, heightAt(x, z) + 0.02, z);
  group.rotation.y = yaw;
  ctx.propsGroup.add(group);
  return group;
}

// A short fishing pier jutting out over a pond: deck planks on two posts.
export function buildDock(ctx, x, z, yaw = 0, length = 2.4) {
  const g = buildFootbridge(ctx, x, z, yaw, length, 1.1);
  return g;
}

// Called from the main loop. Slow surface shimmer on every water body + a gentle
// twinkle on the glints. Cheap: a handful of materials, no allocation.
export function updateWater(dt, t) {
  for (let i = 0; i < _waters.length; i++) {
    const w = _waters[i];
    w.mesh.material.opacity = w.baseOpacity + Math.sin(t * 1.3 + i) * 0.05;
  }
  for (let i = 0; i < _sharedGlints.length; i++) {
    const gl = _sharedGlints[i];
    gl.sprite.material.opacity = gl.base * (0.5 + 0.5 * Math.sin(t * 2.2 + gl.phase));
  }
}
